import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatMoney, formatQty } from '../api';
import { Badge, Card, CheckField, Empty, Loading, Notice, TextField } from '../components/ui';
import type { Partner } from '../types';

interface Kalkulation {
  vkBruttoCents: number | null;
  vkNettoCents: number | null;
  einstandCents: number | null;
  einstandMitAvuCents: number | null;
  avuCents: number;
  rohertragCents: number | null;
  rohertragMitAvuCents: number | null;
  spanne: number | null;
  spanneMitAvu: number | null;
  aufschlag: number | null;
  aufschlagMitAvu: number | null;
  fehlt: string[];
}

interface Zeile {
  id: string;
  sku: string;
  name: string;
  hersteller_id: string | null;
  hersteller: string | null;
  lieferant: string | null;
  bestand: number;
  steuersatz: number;
  vk_brutto_cents: number | null;
  einstand_cents: number | null;
  avu_cents: number;
  avu_bezeichnung: string | null;
  kalkulation: Kalkulation;
}

const prozent = (wert: number | null): string =>
  wert === null ? '—' : `${wert.toFixed(1).replace('.', ',')} %`;

/** Ab wann eine Spanne im Elektrohandel eng wird. Nur eine Einfärbung, keine Regel. */
function ampel(spanne: number | null): 'ok' | 'warn' | 'bad' | 'default' {
  if (spanne === null) return 'default';
  if (spanne < 8) return 'bad';
  if (spanne < 15) return 'warn';
  return 'ok';
}

export function MarginPage() {
  const [zeilen, setZeilen] = useState<Zeile[] | null>(null);
  const [partner, setPartner] = useState<Partner[]>([]);
  const [hersteller, setHersteller] = useState('');
  const [lieferant, setLieferant] = useState('');
  const [suche, setSuche] = useState('');
  const [nurLuecken, setNurLuecken] = useState(false);

  const [einstand, setEinstand] = useState('120,00');
  const [ziel, setZiel] = useState('25');
  const [satz, setSatz] = useState('19');
  const [zielpreis, setZielpreis] = useState<number | null>(null);

  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    const frage = new URLSearchParams();
    if (hersteller) frage.set('herstellerId', hersteller);
    if (lieferant) frage.set('lieferantId', lieferant);
    if (suche.trim()) frage.set('suche', suche.trim());
    if (nurLuecken) frage.set('nurLuecken', 'true');
    try {
      setZeilen(await api<Zeile[]>(`/api/articles/kalkulation?${frage.toString()}`));
    } catch (err) {
      setError(errorText(err));
    }
  }, [hersteller, lieferant, suche, nurLuecken]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void api<Partner[]>('/api/partners').then(setPartner).catch(() => setPartner([]));
  }, []);

  async function rechneZielpreis() {
    const cents = Math.round(Number(einstand.replace(',', '.')) * 100);
    try {
      const ergebnis = await api<{ vkBruttoCents: number | null }>(
        '/api/articles/kalkulation/zielpreis',
        {
          method: 'POST',
          body: {
            einstandCents: cents,
            zielspanne: Number(ziel.replace(',', '.')),
            steuersatz: Number(satz.replace(',', '.')),
          },
        },
      );
      setZielpreis(ergebnis.vkBruttoCents);
    } catch (err) {
      setError(errorText(err));
    }
  }

  if (!zeilen) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  const mitAvu = zeilen.filter((z) => z.avu_cents > 0);
  const unterDeckung = zeilen.filter(
    (z) => z.kalkulation.spanne !== null && z.kalkulation.spanne < 8,
  );

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}

      <Card title="Auswahl">
        <div className="form-grid">
          <label className="field">
            <span>Hersteller</span>
            <select value={hersteller} onChange={(e) => setHersteller(e.target.value)}>
              <option value="">alle</option>
              {partner
                .filter((p) => (p as unknown as { is_manufacturer?: boolean }).is_manufacturer)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="field">
            <span>Lieferant</span>
            <select value={lieferant} onChange={(e) => setLieferant(e.target.value)}>
              <option value="">alle</option>
              {partner
                .filter((p) => p.is_supplier)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </label>
          <TextField label="Suche" value={suche} onChange={setSuche} placeholder="Artikel oder Nummer" />
        </div>
        <CheckField
          label="Nur Artikel mit fehlenden Preisen"
          checked={nurLuecken}
          onChange={setNurLuecken}
        />
      </Card>

      {unterDeckung.length > 0 && !nurLuecken && (
        <Notice kind="error">
          <strong>{unterDeckung.length} Artikel liegen unter 8 % Spanne.</strong> Bei einigen
          davon trägt die Abverkaufsunterstützung den Unterschied — in der Spalte daneben steht,
          bei welchen.
        </Notice>
      )}

      <Card title="Handelsspanne" padded={false}>
        <div className="body small muted" style={{ paddingBottom: 0 }}>
          Die <strong>Spanne</strong> bezieht sich auf den Verkaufspreis, der{' '}
          <strong>Aufschlag</strong> auf den Einkaufspreis — 50 % Aufschlag sind 33 % Spanne.
          Beide Zahlen sind richtig und meinen Verschiedenes. Gerechnet wird immer netto; der
          Ladenpreis wird dafür um die Umsatzsteuer bereinigt.
          {mitAvu.length > 0 && ` ${mitAvu.length} Artikel haben eine laufende Unterstützung.`}
        </div>
        {zeilen.length === 0 ? (
          <Empty>Kein Artikel passt zur Auswahl.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th>Hersteller</th>
                  <th className="num">Einstand</th>
                  <th className="num">VK brutto</th>
                  <th className="num">VK netto</th>
                  <th className="num">Rohertrag</th>
                  <th className="num">Spanne</th>
                  <th className="num">Aufschlag</th>
                  <th className="num">AVU</th>
                  <th className="num">Spanne mit AVU</th>
                </tr>
              </thead>
              <tbody>
                {zeilen.map((z) => {
                  const k = z.kalkulation;
                  return (
                    <tr key={z.id}>
                      <td>
                        <span className="mono">{z.sku}</span> {z.name}
                        {k.fehlt.length > 0 && (
                          <div className="small" style={{ color: 'var(--warn)' }}>
                            es fehlt: {k.fehlt.join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="small">
                        {z.hersteller ?? <span className="muted">—</span>}
                        {z.lieferant && <div className="muted">über {z.lieferant}</div>}
                      </td>
                      <td className="num">{k.einstandCents === null ? '—' : formatMoney(k.einstandCents)}</td>
                      <td className="num">{k.vkBruttoCents === null ? '—' : formatMoney(k.vkBruttoCents)}</td>
                      <td className="num muted">{k.vkNettoCents === null ? '—' : formatMoney(k.vkNettoCents)}</td>
                      <td className="num">
                        {k.rohertragCents === null ? '—' : formatMoney(k.rohertragCents)}
                      </td>
                      <td className="num">
                        <Badge kind={ampel(k.spanne)}>{prozent(k.spanne)}</Badge>
                      </td>
                      <td className="num muted">{prozent(k.aufschlag)}</td>
                      <td className="num">
                        {z.avu_cents > 0 ? (
                          <>
                            {formatMoney(z.avu_cents)}
                            <div className="small muted">{z.avu_bezeichnung}</div>
                          </>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="num">
                        {k.spanneMitAvu === null ? (
                          <span className="muted">—</span>
                        ) : (
                          <Badge kind={ampel(k.spanneMitAvu)}>{prozent(k.spanneMitAvu)}</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="body small muted">
          {zeilen.length} Artikel · Bestand insgesamt{' '}
          {formatQty(zeilen.reduce((summe, z) => summe + Number(z.bestand), 0))}
        </div>
      </Card>

      <Card title="Welcher Preis ergibt welche Spanne?">
        <div className="small muted" style={{ marginBottom: 12 }}>
          Die Umkehrung der Rechnung — für das Einlisten eines neuen Artikels.
        </div>
        <div className="form-grid">
          <TextField label="Einstand netto" value={einstand} onChange={setEinstand} hint="in Euro" />
          <TextField label="Zielspanne" value={ziel} onChange={setZiel} hint="in Prozent vom VK" />
          <TextField label="Steuersatz" value={satz} onChange={setSatz} hint="19 oder 7" />
        </div>
        <button onClick={rechneZielpreis}>Ladenpreis berechnen</button>
        {zielpreis !== null && (
          <div style={{ marginTop: 14, fontSize: 20 }} className="mono">
            {formatMoney(zielpreis)}{' '}
            <span className="small muted">Ladenpreis inkl. {satz} % USt.</span>
          </div>
        )}
      </Card>
    </>
  );
}
